import { useCallback, useEffect, useState } from 'react';
import {
  buildQuickCreateProposalPayload,
  extractCreatedProposalId,
} from '../adapters/workspaceCreateAdapter';
import { CAMPAIGN_COPY } from '../constants/campaignCopy';
import { workspaceService } from '../services/workspaceService';

const EMPTY_FORM = {
  name: '',
  tentativeBudget: '',
  costOfLeads: '',
};

function validateQuickCreateForm(form) {
  const errors = {};

  if (!String(form.name || '').trim()) {
    errors.name = CAMPAIGN_COPY.create.validation.nameRequired;
  }

  const budget = String(form.tentativeBudget ?? '').trim();
  if (!budget) {
    errors.tentativeBudget = CAMPAIGN_COPY.create.validation.tentativeBudgetRequired;
  } else if (Number.isNaN(Number(budget)) || Number(budget) < 0) {
    errors.tentativeBudget = CAMPAIGN_COPY.create.validation.tentativeBudgetInvalid;
  }

  const costOfLeads = String(form.costOfLeads ?? '').trim();
  if (!costOfLeads) {
    errors.costOfLeads = CAMPAIGN_COPY.create.validation.costOfLeadsRequired;
  } else if (Number.isNaN(Number(costOfLeads)) || Number(costOfLeads) < 0) {
    errors.costOfLeads = CAMPAIGN_COPY.create.validation.costOfLeadsInvalid;
  }

  return errors;
}

function extractSubmitError(response) {
  const errorPayload = response?.data?.data ?? response?.data;
  const message =
    errorPayload?.general_error?.name?.[0]
    || errorPayload?.general_error
    || errorPayload?.name?.[0]
    || errorPayload?.message;

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return CAMPAIGN_COPY.create.errors.createFailed;
}

export default function useWorkspaceCreateForm({
  open,
  accountId,
  organisationId,
  onCreated,
  onClose,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      setIsSubmitting(false);
      setSubmitError('');
    }
  }, [open]);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    setSubmitError('');
  }, []);

  const submit = useCallback(async () => {
    const nextErrors = validateQuickCreateForm(form);
    const hasErrors = Object.keys(nextErrors).length > 0;

    setFieldErrors(nextErrors);
    if (hasErrors) {
      return { success: false };
    }

    if (!accountId) {
      setSubmitError(CAMPAIGN_COPY.create.errors.accountMissing);
      return { success: false };
    }

    if (!organisationId) {
      setSubmitError(CAMPAIGN_COPY.create.errors.organisationMissing);
      return { success: false };
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = buildQuickCreateProposalPayload({
        name: form.name,
        tentativeBudget: form.tentativeBudget,
        costOfLeads: form.costOfLeads,
        accountId,
        organisationId,
      });

      const response = await workspaceService.createInitialProposal(accountId, payload);

      if (response?.status === 200 || response?.status === 201) {
        const proposalId = extractCreatedProposalId(response);
        const created = {
          id: proposalId,
          name: payload.name,
          raw: response?.data,
        };

        await onCreated?.(created);
        onClose?.();
        return { success: true, data: created };
      }

      const message = extractSubmitError(response);
      setSubmitError(message);
      return { success: false, message };
    } catch (err) {
      const message =
        err?.response?.data?.data?.general_error
        || err?.response?.data?.name?.[0]
        || err?.response?.data?.message
        || err?.message
        || CAMPAIGN_COPY.create.errors.createFailed;

      setSubmitError(typeof message === 'string' ? message : CAMPAIGN_COPY.create.errors.createFailed);
      return { success: false, message };
    } finally {
      setIsSubmitting(false);
    }
  }, [accountId, form, onClose, onCreated, organisationId]);

  return {
    form,
    fieldErrors,
    isSubmitting,
    submitError,
    updateField,
    submit,
  };
}
